import { useEffect, useRef, useState } from 'react';

/**
 * Lightweight realtime hook for customer + public-stay surfaces (sub-spec 01c
 * + 01d). Subscribes to `/api/realtime/connect?stayToken=...` when a token is
 * provided (public live-stay page); otherwise it opens a cookie-authed
 * customer socket (`?bookingId=...`) that the kc_session cookie authenticates.
 * Re-fetches a counter (`nonce`) every time a BOOKING_MESSAGE_POSTED or
 * BOOKING_PHOTO_POSTED event lands for the matching bookingId.
 *
 * The hook is intentionally minimal — it does not maintain its own message /
 * photo state. Consumers pass the nonce into child components (MessagesThread,
 * PhotoTimeline) so they re-fetch on a bump.
 *
 * Failure mode: when the WS connection fails (preview build with no API, an
 * isolated env, etc.) the nonce simply never bumps and components fall back
 * to their initial fetch. Hosts that want a visible "live" indicator should
 * inspect `status`.
 */

export interface UseStayRealtimeOptions {
  bookingId: string;
  /** Public live-stay token. When set, the hook attempts a token-scoped WS. */
  shareToken?: string | undefined;
  /**
   * When false the hook does NOT open a connection (parent is hidden). The
   * `nonce` value stays at its last seen number.
   */
  enabled?: boolean;
}

export type StayRealtimeStatus = 'idle' | 'connecting' | 'connected' | 'failed';

export interface UseStayRealtimeResult {
  /** Bumps on every relevant event for the watched bookingId. */
  nonce: number;
  status: StayRealtimeStatus;
}

interface RealtimeEvent {
  type: string;
  booking_id?: string;
}

export function useStayRealtime(opts: UseStayRealtimeOptions): UseStayRealtimeResult {
  const enabled = opts.enabled !== false;
  const [nonce, setNonce] = useState(0);
  const [status, setStatus] = useState<StayRealtimeStatus>('idle');
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof WebSocket === 'undefined') return;
    if (typeof window === 'undefined') return;

    setStatus('connecting');
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = new URL(`${proto}//${window.location.host}/api/realtime/connect`);
    if (opts.shareToken) {
      // Public live-stay: the share token in the query is the auth + the
      // server resolves the booking from it (no bookingId query needed).
      url.searchParams.set('stayToken', opts.shareToken);
    } else if (opts.bookingId) {
      // Signed-in customer: the kc_session cookie authenticates; pass the
      // bookingId so the server constrains delivery to this booking.
      url.searchParams.set('bookingId', opts.bookingId);
    }
    let ws: WebSocket;
    try {
      ws = new WebSocket(url.toString());
    } catch {
      setStatus('failed');
      return;
    }
    wsRef.current = ws;

    ws.addEventListener('open', () => setStatus('connected'));
    ws.addEventListener('error', () => setStatus('failed'));
    ws.addEventListener('close', () =>
      setStatus((prev) => (prev === 'connected' ? 'failed' : prev)),
    );
    ws.addEventListener('message', (e) => {
      try {
        const data = JSON.parse(typeof e.data === 'string' ? e.data : '') as RealtimeEvent;
        if (!data || typeof data.type !== 'string') return;
        if (
          data.type !== 'BOOKING_MESSAGE_POSTED' &&
          data.type !== 'BOOKING_PHOTO_POSTED' &&
          data.type !== 'REPORT_CARD_SENT'
        ) {
          return;
        }
        if (data.booking_id && data.booking_id !== opts.bookingId) return;
        setNonce((n) => n + 1);
      } catch {
        /* drop malformed frames */
      }
    });

    return () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }, [enabled, opts.bookingId, opts.shareToken]);

  return { nonce, status };
}
